import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Sparkles } from "lucide-react";
import { isPromoActive, isPromoDismissed, dismissPromo, goToPricing } from "@/utils/promo";
import { trackEvent } from "@/utils/analytics";

const PromoPopup = () => {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isPromoActive() || isPromoDismissed()) return undefined;
    const t = setTimeout(() => {
      setVisible(true);
      trackEvent("promo_popup_view");
    }, 1400);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    dismissPromo();
    setVisible(false);
  };

  const discover = () => {
    trackEvent("promo_cta_click", { location: "popup" });
    close();
    goToPricing(navigate);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="promo-popup"
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-navy/45 backdrop-blur-sm" onClick={close} aria-hidden="true" />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Offre de septembre"
            className="relative w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-lift sm:p-10"
            initial={{ opacity: 0, y: 32, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="pointer-events-none absolute -top-20 left-1/2 h-48 w-72 -translate-x-1/2 rounded-full bg-turquoise/15 blur-[80px]" aria-hidden="true" />
            <button
              data-testid="promo-popup-close"
              onClick={close}
              aria-label="Fermer"
              className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full text-navy/40 transition-colors hover:bg-mist hover:text-navy"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-turquoise/10 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.16em] text-turquoise">
                <Sparkles className="h-3.5 w-3.5" /> Avantage Septembre
              </span>
              <p className="mt-6 font-mono text-4xl font-bold tracking-tight text-navy sm:text-5xl">
                390 € HT
                <span className="mt-1 block font-sans text-2xl font-extrabold uppercase tracking-wide text-turquoise sm:text-3xl">offerts</span>
              </p>
              <p className="mt-5 text-sm leading-relaxed text-navy/65 sm:text-base">
                Jusqu'au 30 septembre, profitez de la mise en service et du paramétrage
                de votre espace ActiPilote offerts.
              </p>
              <p className="mt-3 text-xs font-semibold text-navy/45">Abonnements à partir de 99 € HT/mois</p>

              <div className="mt-7 flex flex-col gap-2.5">
                <button
                  data-testid="promo-popup-primary-cta"
                  onClick={discover}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-bold text-white shadow-glow transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-700"
                >
                  Découvrir l'offre
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
                <button
                  data-testid="promo-popup-secondary-cta"
                  onClick={close}
                  className="w-full rounded-full px-6 py-3 text-sm font-bold text-navy/60 transition-colors hover:bg-mist hover:text-navy"
                >
                  Continuer sur le site
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PromoPopup;
